import { ChangeEvent, useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import AddAPhoto from "@mui/icons-material/AddAPhoto";

import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Fab from "@mui/material/Fab";
import Badge from "@mui/material/Badge";
import Paper from "@mui/material/Paper";
import { useAppDispatch } from "../../../app/hooks";
import Box from "@mui/material/Box";
import Toast from "../../../components/Layout/Toast";

import AuthSkeleton from "../../auth/AuthSkeleton";
import { resizeFile } from "../../../utils/imageUtility";
import { uploadCompanyPicture } from "../setupSlices";
import { PhotoTypes } from "../../process/types/processTypes";
import { Company } from "../types/setupTypes";
import { apolloClient } from "../../../apollo/graphql";
import { GET_SELECTED_COMPANY } from "../../../apollo/queries";
import { Typography } from "@mui/material";

interface CompanyProps {
  companyId: string;
}

function TestimonialSignatures({ companyId }: CompanyProps) {
  const [signatureImage, setSignatureImage] = useState("");
  const [secondSignatureImage, setSecondSignatureImage] = useState("");
  const [currentCompany, setCurrentCompany] = useState<Company | undefined>();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // Initial fetch when component mounts
  useEffect(() => {
    if (companyId) {
      const fetchCompanyData = async () => {
        try {
          const response = await apolloClient.query({
            query: GET_SELECTED_COMPANY,
            variables: { id: companyId },
          });

          if (response && response.data && response.data.getCompany) {
            setCurrentCompany(response.data.getCompany as Company);
          } else {
            console.log(
              `TestimonialSignatures: No company data found for ID: ${companyId}`
            );
          }
        } catch (error) {
          console.error(
            `TestimonialSignatures: Error fetching company for ID ${companyId}:`,
            error
          );
        }
      };

      fetchCompanyData();
    }
  }, [companyId]);

  const handleUpload = async (
    image: File,
    type: PhotoTypes,
    companyName: string
  ) => {
    if (!currentCompany) return;

    setLocalLoading(true);
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const result = await dispatch(
        uploadCompanyPicture({
          _id: currentCompany._id as string,
          type: type,
          avatar: image,
        })
      ).unwrap();

      setLocalSuccess(
        `${currentCompany.displayName} ${type} uploaded successfully`
      );
      setUploadSuccess(true);
    } catch (error: any) {
      setLocalError(error.message || "Upload failed");
    } finally {
      setLocalLoading(false);
    }
  };

  const onChangeFirstTestimonialSignature = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { validity, files } = event.target;

    if (validity.valid && files && files[0]) {
      setSignatureImage(URL.createObjectURL(files[0]) as string);
      const image = (await resizeFile(files[0], 80, 150)) as File;
      if (image && currentCompany) {
        await handleUpload(
          image,
          PhotoTypes.TestimonySignature,
          currentCompany.displayName
        );
      }
    }
  };
  const onChangeSecondTestimonialSignature = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { validity, files } = event.target;

    if (validity.valid && files && files[0]) {
      setSecondSignatureImage(URL.createObjectURL(files[0]) as string);
      const image = (await resizeFile(files[0], 80, 150)) as File;
      if (image && currentCompany) {
        await handleUpload(
          image,
          PhotoTypes.TestimonySignature2,
          currentCompany.displayName
        );
      }
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        style={{
          position: "relative",
          borderRadius: 18,
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          gap: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          {currentCompany && currentCompany._id && (
            <Typography variant="h6">{" First"}</Typography>
          )}
          <input
            name={`${companyId}_firstTestimonialSignatureImage`}
            accept="image/*"
            onChange={onChangeFirstTestimonialSignature}
            style={{ display: "none" }}
            id={`${companyId}_icon-button-file-first-testimonial-signature`}
            type="file"
          />
          <label
            htmlFor={`${companyId}_icon-button-file-first-testimonial-signature`}
          >
            <>
              <Badge
                overlap="rectangular"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                badgeContent={
                  <Tooltip
                    title="Add/Change First Testimonial Signature"
                    aria-label={`${companyId}_icon-button-file-first-testimonial-signature`}
                  >
                    <Fab component="span" color="primary">
                      <IconButton
                        aria-label={`${companyId}_icon-button-file-first-testimonial-signature`}
                        component="span"
                        size="small"
                        color="inherit"
                      >
                        <AddAPhoto />
                      </IconButton>
                    </Fab>
                  </Tooltip>
                }
              >
                {localLoading ? (
                  <AuthSkeleton />
                ) : (
                  <Avatar
                    alt="avatar"
                    variant="rounded"
                    src={
                      signatureImage || currentCompany?.testimonySignature || ""
                    }
                    sx={{
                      height: 80,
                      width: 150,
                    }}
                  />
                )}
              </Badge>
            </>
          </label>{" "}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          {currentCompany && currentCompany._id && (
            <Typography variant="h6">{" Second "}</Typography>
          )}
          <input
            name={`${companyId}_secondTestimonialSignatureImage`}
            accept="image/*"
            onChange={onChangeSecondTestimonialSignature}
            style={{ display: "none" }}
            id={`${companyId}_icon-button-file-second-testimonial-signature`}
            type="file"
          />
          <label
            htmlFor={`${companyId}_icon-button-file-second-testimonial-signature`}
          >
            <>
              <Badge
                overlap="rectangular"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                badgeContent={
                  <Tooltip
                    title="Add/Change Second Testimonial Signature"
                    aria-label={`${companyId}_icon-button-file-second-testimonial-signature`}
                  >
                    <Fab component="span" color="primary">
                      <IconButton
                        aria-label={`${companyId}_icon-button-file-second-testimonial-signature`}
                        component="span"
                        size="small"
                        color="inherit"
                      >
                        <AddAPhoto />
                      </IconButton>
                    </Fab>
                  </Tooltip>
                }
              >
                {localLoading ? (
                  <AuthSkeleton />
                ) : (
                  <Avatar
                    alt="avatar"
                    variant="rounded"
                    src={
                      secondSignatureImage ||
                      currentCompany?.testimonySignature2 ||
                      ""
                    }
                    sx={{
                      height: 80,
                      width: 150,
                    }}
                  />
                )}
              </Badge>
            </>
          </label>{" "}
        </Box>
        <Box m={1}>
          {localError && <Toast severity="error">{localError}</Toast>}
          {localSuccess && <Toast severity="success">{localSuccess}</Toast>}
        </Box>
      </Paper>
    </>
  );
}

export default TestimonialSignatures;
